import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import { Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
// import { testUpcomingGames } from "../../assets/testData";
import _ from "lodash";
import { getCountryInfo } from "../../utils/countryUtils";
import { useContestId } from "../../hooks/useContestId";
import { useNavigate, useLocation } from "react-router";

const StatusBar = (props) => {
  const { userStatus } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const contestId = useContestId();
  const location = useLocation();

  const timezone = useSelector((state) => state.app?.tz);
  const upcomingGames = useSelector((state) => state.app?.upcomingGames);
  const groupGames = useSelector((state) => state.games?.groupGames);
  const knockoutGames = useSelector((state) => state.games?.knockoutGames);

  // On the Games page the strip becomes the FULL contest schedule (first game
  // to last) so it scrolls from the start of the contest to the end. The games
  // endpoints nest team data under team_1/team_2; the strip renders the flat
  // shape /status uses, so normalize to that and read either shape defensively.
  // TBD knockout slots (no teams yet) are dropped from the Games page's full
  // schedule (below) but shown as TBD placeholders everywhere else the strip
  // renders — matching the convention already used in Results.js.
  const onGamesPage = location.pathname.startsWith("/games");

  const fullSchedule = useMemo(() => {
    const flatten = (grouped) => _.flatten(_.toArray(grouped));
    const normalize = (g) => ({
      match_datetime_utc: g?.match_datetime_utc,
      game_status: g?.game_status,
      team_1_name: g?.team_1?.country_name ?? g?.team_1_name,
      team_1_flag: g?.team_1?.flag_image_url ?? g?.team_1_flag,
      team_1_goals: g?.team_1?.goals ?? g?.team_1_goals,
      team_2_name: g?.team_2?.country_name ?? g?.team_2_name,
      team_2_flag: g?.team_2?.flag_image_url ?? g?.team_2_flag,
      team_2_goals: g?.team_2?.goals ?? g?.team_2_goals,
    });
    return [...flatten(groupGames), ...flatten(knockoutGames)]
      .map(normalize)
      .filter(
        (g) =>
          g.match_datetime_utc &&
          g.team_1_name &&
          g.team_2_name &&
          g.team_1_name !== "TBD" &&
          g.team_2_name !== "TBD",
      )
      .sort(
        (a, b) =>
          Date.parse(a.match_datetime_utc) - Date.parse(b.match_datetime_utc),
      );
  }, [groupGames, knockoutGames]);

  // Full schedule on the Games page (when loaded); the /status window otherwise.
  const stripGames =
    onGamesPage && fullSchedule.length ? fullSchedule : upcomingGames || [];

  const getScheduledTime = useCallback(
    (game) => {
      const tzMoment = moment.utc(game?.match_datetime_utc).tz(timezone);
      if (!!tzMoment) {
        const dateFormat = tzMoment?.calendar(null, {
          sameDay: "[Today]",
          nextDay: "[Tomorrow]",
          nextWeek: "ddd MM/D",
          lastDay: "[Yesterday]",
          lastWeek: "[Last] dddd",
          sameElse: "DD/MM",
        });
        const timeFormat = tzMoment.format("h:mm A");
        return `${dateFormat} • ${timeFormat}`;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timezone, userStatus],
  );

  return (
    <Grid
      container
      direction="column"
      sx={[
        {
          position: "relative",
          width: "100%",
          padding: "16px",
          boxShadow: ` 0 2px 20px -4px ${theme.branding.gray[300]}`,
          clipPath: `inset(0px -10px -30px -10px)`,
          backgroundColor: theme.branding.gray[50],
        },
        (theme) =>
          theme.applyStyles("dark", {
            backgroundColor: theme.branding.darkBlue[800],
            borderColor: theme.branding.darkBlue[600],
            boxShadow: `3px 4px 30px -3px ${theme.branding.cyan[800]}, 0 4px 30px -4px ${theme.branding.darkBlue[900]}`,
          }),
      ]}
    >
      <Grid
        container
        direction="column"
        sx={{
          position: "relative",
          maxWidth: "100%",
          overflow: "scroll",
          scrollbarWidth: "none",
          paddingLeft: isMobile ? "12px" : "16px",
          paddingRight: isMobile ? "12px" : "30px",
        }}
      >
        <Grid
          container
          direction="row"
          wrap="nowrap"
          sx={{
            position: "relative",
            overflow: "scroll",
            whiteSpace: "nowrap",
            scrollbarWidth: "none",
            gap: "12px",
          }}
        >
          {stripGames?.map((game, idx) => {
            const hasTeam1 = !!game?.team_1_name;
            const hasTeam2 = !!game?.team_2_name;
            return (
            <Grid
              key={`strip-${idx}-${game?.match_datetime_utc}`}
              container
              direction="column"
              onClick={() => {
                const unixGameTime = Date.parse(game?.match_datetime_utc);
                // Preserve the contest the strip is showing — otherwise the
                // Games body falls back to the default contest and renders a
                // different set of games than the thumbnail the user clicked.
                navigate(
                  `/games?matchdate=${unixGameTime}` +
                    (contestId ? `&contest_id=${contestId}` : ""),
                );
              }}
              sx={[
                {
                  padding: "10px 16px",
                  gap: "6px",
                  position: "relative",
                  borderRadius: "14px",
                  border: `1px solid ${game?.game_status === "InProgress" ? theme.branding.cyan[200] : theme.branding.gray[200]}`,
                  "&:hover": {
                    cursor: "pointer",
                  },
                },
                (theme) =>
                  theme.applyStyles("dark", {
                    border: `1px solid ${game?.game_status === "InProgress" ? theme.branding.cyan[200] : theme.branding.darkBlue[300]}`,
                  }),
              ]}
            >
              <Grid
                container
                direction="row"
                wrap="nowrap"
                sx={{
                  gap: "12px",
                  justifyContent: "space-between",
                  alignItems: "center",
                  zIndex: 20,
                }}
              >
                {/* team 1 */}
                <Grid
                  container
                  direction="row"
                  wrap="nowrap"
                  sx={{ gap: "8px", alignItems: "center" }}
                >
                  <Grid
                    container
                    sx={[
                      {
                        maxWidth: "28px",
                        width: "fit-content",
                        justifyContent: "center",
                        alignItems: "center",
                        boxShadow: ` 0px 0px 1px 0.5px ${theme.branding.gray[300]}`,
                      },
                      (theme) =>
                        theme.applyStyles("dark", {
                          boxShadow: ` 0px 0px 1px 0.5px ${theme.branding.darkBlue[300]}`,
                        }),
                    ]}
                  >
                    {game?.team_1_flag ? (
                      <img
                        src={game?.team_1_flag}
                        alt={`status-bar-${game?.team_1_name}-country-flag-icon`}
                        style={{ maxWidth: "28px", maxHeight: "20px" }}
                      />
                    ) : (
                      <Grid
                        sx={{
                          width: "28px",
                          height: "20px",
                          borderRadius: "2px",
                          backgroundColor: theme.branding.gray[200],
                        }}
                      />
                    )}
                  </Grid>
                  <Typography variant="subtitle2" sx={{ lineHeight: "28px" }}>
                    {hasTeam1 ? getCountryInfo(game?.team_1_name)?.code : "TBD"}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={[
                      {
                        color:
                          game?.team_1_goals >= game?.team_2_goals &&
                          game?.team_1_goals > 0
                            ? theme.branding.cyan[500]
                            : theme.branding.gray[500],
                      },
                      (theme) =>
                        theme.applyStyles("dark", {
                          color:
                            game?.team_1_goals >= game?.team_2_goals &&
                            game?.team_1_goals > 0
                              ? theme.branding.cyan[500]
                              : theme.branding.gray[400],
                        }),
                    ]}
                  >
                    {game?.team_1_goals}
                  </Typography>
                </Grid>

                <Typography
                  variant="caption"
                  sx={{ color: theme.branding.gray[400], textAlign: "center" }}
                >
                  {game?.game_status !== "Scheduled" ? "—" : "VS"}
                </Typography>

                {/* team 2 */}
                <Grid
                  container
                  direction="row"
                  wrap="nowrap"
                  sx={{ gap: "8px", alignItems: "center" }}
                >
                  <Typography
                    variant="h5"
                    sx={[
                      {
                        color:
                          game?.team_2_goals >= game?.team_1_goals &&
                          game?.team_2_goals > 0
                            ? theme.branding.cyan[500]
                            : theme.branding.gray[500],
                      },
                      (theme) =>
                        theme.applyStyles("dark", {
                          color:
                            game?.team_2_goals >= game?.team_1_goals &&
                            game?.team_2_goals > 0
                              ? theme.branding.cyan[500]
                              : theme.branding.gray[400],
                        }),
                    ]}
                  >
                    {game?.team_2_goals}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ lineHeight: "28px" }}>
                    {hasTeam2 ? getCountryInfo(game?.team_2_name)?.code : "TBD"}
                  </Typography>
                  <Grid
                    container
                    sx={[
                      {
                        maxWidth: "28px",
                        width: "fit-content",
                        justifyContent: "center",
                        alignItems: "center",
                        boxShadow: ` 0px 0px 1px 0.5px ${theme.branding.gray[300]}`,
                      },
                      (theme) =>
                        theme.applyStyles("dark", {
                          boxShadow: ` 0px 0px 1px 0.5px ${theme.branding.darkBlue[300]}`,
                        }),
                    ]}
                  >
                    {game?.team_2_flag ? (
                      <img
                        src={game?.team_2_flag}
                        alt={`status-bar-${game?.team_2_name}-country-flag-icon`}
                        style={{ maxWidth: "28px", maxHeight: "20px" }}
                      />
                    ) : (
                      <Grid
                        sx={{
                          width: "28px",
                          height: "20px",
                          borderRadius: "2px",
                          backgroundColor: theme.branding.gray[200],
                        }}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid container sx={{ justifyContent: "center", zIndex: 20 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.branding.gray[500],
                    fontWeight:
                      game?.game_status !== "Scheduled" ? 700 : "auto",
                  }}
                >
                  {game?.game_status === "Final" ? (
                    "FT"
                  ) : game?.game_status === "InProgress" ? (
                    <span style={{ color: theme.branding.cyan[500] }}>
                      ● {}'
                    </span>
                  ) : (
                    getScheduledTime(game)
                  )}
                </Typography>
              </Grid>
              <Grid
                sx={[
                  {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    borderRadius: "14px",
                    height: "100%",
                    width: "100%",
                    backgroundColor:
                      game?.game_status === "InProgress"
                        ? theme.branding.cyan[300]
                        : theme.branding.gray[0],
                    opacity: game?.game_status === "InProgress" ? 0.03 : 1,
                  },
                  (theme) =>
                    theme.applyStyles("dark", {
                      backgroundColor:
                        game?.game_status === "InProgress"
                          ? theme.branding.cyan[300]
                          : theme.branding.darkBlue[800],
                    }),
                ]}
              />
            </Grid>
            );
          })}
        </Grid>
      </Grid>
      <Grid
        sx={{
          width: "100%",
          height: "100%",
          background: isMobile
            ? `linear-gradient(90deg, ${theme.branding.gray[50]} 5%, transparent 8%, transparent 92%, ${theme.branding.gray[50]} 95%);`
            : `linear-gradient(90deg, ${theme.branding.gray[50]} 1.5%, transparent 3%, transparent 94%, ${theme.branding.gray[50]} 98%);`,
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 100,
          pointerEvents: "none",
        }}
      />
    </Grid>
  );
};

export default StatusBar;
